import {type IPeachPayCustomer} from '../../../@type/peachpay/customer';
import {renderDropDownList, selectDropdown} from '../util/ui';
import {cleanFormFields, formEntry, isRequiredField, isShortAddressEmpty} from '../util/form';
import {stateProvinceOrCounty} from '../util/country';
import {getLocaleText} from '../util/translation';
import {type WCFieldInfo, type WCLocationInfo} from '../../../@type/woocommerce/location-info';
import {store} from '../store';
import {Environment, Feature, startModalLoading, stopModalLoading, updateEnvironment} from '../reducers/environmentReducer';
import {MerchantConfiguration} from '../reducers/merchantConfigurationReducer';
import {type ModalPage} from '../models/IEnvironment';
import {GLOBAL} from '../deprecated/global';
import {clearCustomer, getCustomer, setCustomer} from '../util/customer';
import {Carts} from '../reducers/cartReducer';
import {generateMainForm, generateShippingForm} from './billingShippingFields';
import {type WCFieldsData} from '../../../@type/woocommerce/field-data';
import {requestCartCalculation} from '../payment/order';
import {validateAddress} from './shipping';
import {$qs, $qsAll} from '../../../@shared/ts/dom';
import {type WCPHPData} from '../../../@type/woocommerce/php-data';
import {type InitMessage} from '../../../@type/window-messages';
import {FeatureFlag} from '../../../@type/features';
import {PeachPayCustomer, updateCustomerFields, updateCustomer} from '../reducers/peachPayCustomerReducer';
import {PeachPayOrder} from '../reducers/peachPayOrderReducer';
import {checkRequiredFields} from './additionalFields';

export function installCustomerFormFields(message: InitMessage): void {
	const form = generateMainForm(message);

	const ppInfo = $qs('#pp-info');
	if (ppInfo) {
		ppInfo.insertBefore(form, ppInfo.firstChild);
	}
}

export function installCustomerBillingFields(message: InitMessage): void {
	const newForm = generateShippingForm(message);
	$qs('#shipping-details fieldset')?.appendChild(newForm);
}

export function initCustomer(message: InitMessage) {
	initCustomerEvents(message);
}

function initCustomerEvents(message: InitMessage) {
	$qsAll('.save-payment-info-checkbox', $el => {
		$el?.addEventListener('change', function (this: HTMLInputElement) {
			const isChecked = this.checked;

			store.dispatch(updateEnvironment({
				customerSaveToBrowserSelected: isChecked,
			}));
		});
	});

	initCustomerFormEvents(message);

	// Ensure changes to the customer data get rendered
	let previousShipToDifferentAddress = PeachPayCustomer.shipToDifferentAddress();
	let previousCustomerStr = JSON.stringify(PeachPayCustomer.data());
	let previousOpen = Environment.modalUI.open();
	let previousModalPage = Environment.modalUI.page();

	store.subscribe(async () => {
		if (Environment.modalUI.open()) {
			const customer = PeachPayCustomer.data();
			const shipToDifferentAddress = PeachPayCustomer.shipToDifferentAddress();
			const customerStr = JSON.stringify(customer);
			const modalPage = Environment.modalUI.page();

			// #region Render or re-render the country and correct state/province field in the following cases:
			// * after opening the modal
			// * after expanding the separate shipping fields
			// * after switching to new customer checkout
			if (Environment.modalUI.open() && !previousOpen) {
				renderCountryAndStateList(message.phpData.wc_location_info ?? undefined);
				renderCorrectProvinceField(message.phpData);

				if (Feature.enabled(FeatureFlag.USE_WC_COUNTRY_LOCALE)) {
					setTimeout(() => {
						wcCountryLocaleRuleRender(modalPage);
					});
				}
			}

			if (shipToDifferentAddress && !previousShipToDifferentAddress) {
				renderCountryAndStateList(message.phpData.wc_location_info ?? undefined);

				renderCorrectProvinceField(message.phpData);

				if (Feature.enabled(FeatureFlag.USE_WC_COUNTRY_LOCALE)) {
					setTimeout(() => {
						wcCountryLocaleRuleRender(modalPage);
					});
				}
			}
			// #endregion

			if (modalPage === 'info' || modalPage === 'shipping') {
				renderShippingElements(message);
			}

			if (modalPage === 'shipping') {
				renderCustomerHeader();
			}

			if ((previousCustomerStr !== customerStr || previousModalPage !== modalPage)) {
				renderCustomerFields(customer);
				setCustomer(customer, true);
			}

			previousShipToDifferentAddress = shipToDifferentAddress;
			previousCustomerStr = customerStr;
			previousModalPage = modalPage;
			previousOpen = true;
		} else {
			previousOpen = false;
		}
	});
}

/**
 * Attaches event listeners to form elements so that changes can be synced with the PeachPay data and then reflected in the modal state.
 * Zero Length Timeout is needed at times to ensure that the changes we are seeing are after the change is made and not right before which is the default.
 *
 * @param message The initial message with PHP data.
 */
function initCustomerFormEvents(message: InitMessage) {
	$qs('#pp-info-form')?.addEventListener('change', (event: Event) => {
		// If the country field changed
		const target = event.target as HTMLElement;
		if (target && target.closest('select')?.name === 'billing_country') {
			renderCorrectProvinceField(message.phpData);
			if (Feature.enabled(FeatureFlag.USE_WC_COUNTRY_LOCALE)) {
				setTimeout(() => {
					wcCountryLocaleRuleRender('info');
				});
			}
		}

		setTimeout(() => {
			syncCustomerFieldChanges('#pp-info-form');
		});
	});
	// EDIT button on shipping page
	$qs('#open-shipping-details')?.addEventListener('change', async (event: Event) => {
		if ((event.target as HTMLInputElement).checked) {
			const formFields = PeachPayCustomer.data().form_fields;
			formFields['ship_to_different_address'] = '1';
			store.dispatch(updateCustomerFields(formFields));
		} else {
			const formFields = PeachPayCustomer.data().form_fields;
			// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
			delete formFields['ship_to_different_address'];
			store.dispatch(updateCustomerFields(formFields));
		}

		store.dispatch(startModalLoading());
		await requestCartCalculation();
		store.dispatch(stopModalLoading());
	});
	// shipping form element with shipping fields, including separate shipping address option
	$qs('#pp-different-shipping-details')?.addEventListener('change', (event: Event) => {
		// If the country field changed
		const target = event.target as HTMLElement;
		if (target && target.closest('select')?.name === 'shipping_country') {
			renderCorrectProvinceField(message.phpData);
			if (Feature.enabled(FeatureFlag.USE_WC_COUNTRY_LOCALE)) {
				setTimeout(() => {
					wcCountryLocaleRuleRender('shipping');
				});
			}
		}

		setTimeout(async () => {
			let canMoveOn = false;
			if (!Carts.virtual()) {
				if (PeachPayCustomer.shipToDifferentAddress()) {
					syncCustomerFieldChanges('#pp-different-shipping-details');
				}

				if (checkRequiredFields() && !isShortAddressEmpty('shipping')) {
					store.dispatch(startModalLoading());
					canMoveOn = await validateAddress();
					await requestCartCalculation();
					store.dispatch(stopModalLoading());
				}
			}

			document.querySelectorAll('#pp-continue-to-payment, #pp-continue-to-payment-mobile, #pp-checkout-status-right').forEach($forwardButton => {
				const toggle = $qs('#open-shipping-details');
				if ((toggle as HTMLInputElement).checked) {
					if (canMoveOn && PeachPayOrder.errorMessage() === '') {
						$forwardButton.removeAttribute('disabled');
					} else {
						$forwardButton.setAttribute('disabled', '');
					}
				}
			});
		});
	});
}

/**
 * Renders elements that depend on the customer's shipping eligibility and configuration (virtual cart, hidden shipping fields for virtual products, separate shipping address).
 *
 * @param message The initial message with PHP data.
 */
function renderShippingElements(message: InitMessage) {
	// #open-shipping-details (EDIT button on shipping page)
	const $openShippingDetails = $qs('#open-shipping-details');
	if ($openShippingDetails) {
		($openShippingDetails as HTMLInputElement).checked = !Carts.virtual() && PeachPayCustomer.shipToDifferentAddress();
		($openShippingDetails as HTMLInputElement).disabled = Carts.virtual();
	}

	// #pp-shipping-options (shipping method radio fields)
	if (Carts.virtual()) {
		$qs('#pp-shipping-options')?.classList.add('hide');
	} else {
		$qs('#pp-shipping-options')?.classList.remove('hide');
	}

	// (form fields that affect shipping)
	if (Carts.virtual() && Feature.enabled(FeatureFlag.VIRTUAL_PRODUCT_FIELDS)) {
		hideShippingDetails(true, message.phpData?.billing_fields ?? [], message.phpData?.billing_fields_order ?? []);
	} else {
		hideShippingDetails(false, message.phpData?.billing_fields ?? [], message.phpData?.billing_fields_order ?? []);
	}

	// #shipping-details, $shipping-details fieldset (shipping form container and fieldset)
	if (PeachPayCustomer.shipToDifferentAddress()) {
		$qs('#shipping-details')?.classList.remove('hide');
		$qs('#shipping-details fieldset')?.removeAttribute('disabled');
	} else {
		$qs('#shipping-details')?.classList.add('hide');
		$qs('#shipping-details fieldset')?.setAttribute('disabled', '');
	}

	// #shipping-options-container-address (shipping address preview)
	if (PeachPayCustomer.shipToDifferentAddress()) {
		$qs('#shipping-options-container-address')?.classList.add('pp-opacity-0');
	} else {
		$qs('#shipping-options-container-address')?.classList.remove('pp-opacity-0');
	}

	// #pp-shipping-details-container
	if (Carts.virtual()) {
		$qs('#pp-shipping-details-container')?.classList.add('hide');
	} else {
		$qs('#pp-shipping-details-container')?.classList.remove('hide');
	}
}

/* Hides the address-related billing fields. This function is called if the merchant has
   enabled the setting to hide the address fields for virtual products. */
function hideShippingDetails(hide: boolean, fieldData: WCFieldsData[], fieldOrder: number[]) {
	// The section we need to hide.
	const defaultFieldNames: string[] = [
		'billing_address_1',
		'billing_address_2',
		'billing_postcode',
		'billing_city',
		'billing_state',
		'billing_country',
	];

	const customerDataRequiredFields: string[] = [];
	fieldOrder.forEach(order => {
		const field = fieldData[order];
		if (field && (defaultFieldNames.includes(field.field_name) && field.field_required)) {
			customerDataRequiredFields.push(field.field_name);
		}
	});

	const customerDataEnableFields: string[] = [];
	fieldOrder.forEach(order => {
		const field = fieldData[order];
		if (field && (defaultFieldNames.includes(field.field_name) && field.field_enable)) {
			customerDataEnableFields.push(field.field_name);
		}
	});

	if (hide) {
		customerDataEnableFields.forEach(fieldName => {
			$qsAll<HTMLElement>(`.pp-info-form-new .${fieldName}-field`, $element => {
				$element.classList.add('hide');
			});
		});
	} else if (!hide) {
		customerDataEnableFields.forEach(fieldName => {
			$qsAll<HTMLElement>(`.pp-info-form-new .${fieldName}-field`, $element => {
				$element.classList.remove('hide');
			});
		});
	}

	const info = PeachPayCustomer.billing;
	const stateFieldInfo = GLOBAL.phpData?.wc_location_info?.country_locale_data?.[info.country()]?.['state'];
	const postalFieldInfo = GLOBAL.phpData?.wc_location_info?.country_locale_data?.[info.country()]?.['state'];
	if (hide) {
		customerDataRequiredFields.forEach(fieldName => {
			if ((fieldName === 'billing_country' || fieldName === 'billing_state') && !isRequiredField(fieldName, fieldData)) {
				$qs<HTMLSelectElement>(`.pp-info-form-new .${fieldName}-field select`, $element => {
					$element.removeAttribute('required');
				});
				return;
			}

			$qs<HTMLInputElement>(`.pp-info-form-new .${fieldName}-field input`, $element => {
				$element.removeAttribute('required');
			});
		});
	} else if (!hide) {
		customerDataRequiredFields.forEach(fieldName => {
			if (fieldName === 'billing_country') {
				$qs<HTMLSelectElement>(`.pp-info-form-new .${fieldName}-field select`, $element => {
					$element.setAttribute('required', '');
				});
				return;
			}

			if (fieldName === 'billing_state') {
				$qs<HTMLSelectElement>(`.pp-info-form-new .${fieldName}-field select`, $element => {
					$element.required = isRequiredField(fieldName, fieldData, stateFieldInfo);
				});
				return;
			}

			if (fieldName === 'billing_postcode') {
				$qs<HTMLInputElement>(`.pp-info-form-new .${fieldName}-field input`, $element => {
					$element.required = isRequiredField(fieldName, fieldData, postalFieldInfo);
				});
				return;
			}

			$qs<HTMLInputElement>(`.pp-info-form-new .${fieldName}-field input`, $element => {
				$element.setAttribute('required', '');
			});
		});
	}
}

/* This is used to render the right form fields if the merchant opted in for Woocommerce
   locale overrides. */
export function wcCountryLocaleRuleRender(page: string) {
	let infoFormName: string;
	let section: string;
	if (page === 'shipping') {
		infoFormName = '#pp-different-shipping-details';
		section = 'shipping';
	} else if (page === 'info') {
		infoFormName = '#pp-info-form';
		section = 'billing';
	} else {
		return;
	}

	const countryElement = $qs<HTMLInputElement>(`${infoFormName} [name="${section}_country"]`);
	const country = countryElement?.value;
	if (!country) {
		return;
	}

	const fieldData = section === 'billing' ? GLOBAL.phpData?.billing_fields ?? [] : GLOBAL.phpData?.shipping_fields ?? [];
	const fieldStateFieldInfo = GLOBAL.phpData?.wc_location_info?.country_locale_data[country]?.['state'];
	const fieldPostalCodeFieldInfo = GLOBAL.phpData?.wc_location_info?.country_locale_data[country]?.['postcode'];
	const fieldCityFieldInfo = GLOBAL.phpData?.wc_location_info?.country_locale_data[country]?.['city'];

	if (fieldStateFieldInfo) {
		wcStatefieldRule(fieldStateFieldInfo, section, infoFormName);
	} else {
		unhideFields('state', section, fieldData, infoFormName);
	}

	if (fieldPostalCodeFieldInfo) {
		wcPostalCodeFieldRule(fieldPostalCodeFieldInfo, section, infoFormName);
	} else {
		unhideFields('postcode', section, fieldData, infoFormName);
	}

	if (fieldCityFieldInfo) {
		wcCityFieldRule(fieldCityFieldInfo, section, infoFormName);
	} else {
		unhideFields('city', section, fieldData, infoFormName);
	}
}

function wcStatefieldRule(stateFieldInfo: WCFieldInfo, section: string, formIdentifier: string) {
	const stateField = `${section}_state-field`;
	const fieldData = section === 'billing' ? GLOBAL.phpData?.billing_fields ?? [] : GLOBAL.phpData?.shipping_fields ?? [];
	const fieldIsRequired = isRequiredField(`${section}_state`, fieldData, stateFieldInfo);
	const label = $qs<HTMLLabelElement>(`${formIdentifier} .${stateField} label:not(.hide)`);
	if (label?.textContent) {
		if (fieldIsRequired && /\(.*\)/.exec((label.textContent))) {
			label.textContent?.replace(/[ ]*\(.*\)[ ]*/, '');
		} else if (!fieldIsRequired && !/\(.*\)/.exec((label?.textContent))) {
			label.textContent += ' (optional) ';
		}
	}

	$qs<HTMLInputElement | HTMLSelectElement>(`${formIdentifier} [name="${section}_state"]`, $element => {
		$element.required = fieldIsRequired;
	});
}

function wcPostalCodeFieldRule(postcodeFieldInfo: WCFieldInfo, section: string, formIdentifier: string) {
	const postcodeField = `${section}_postcode-field`;
	const fieldData = section === 'billing' ? GLOBAL.phpData?.billing_fields ?? [] : GLOBAL.phpData?.shipping_fields ?? [];
	// Hide the postal field.
	if (postcodeFieldInfo?.hidden !== undefined && postcodeFieldInfo.hidden) {
		$qs<HTMLElement>(`${formIdentifier} .${postcodeField}`, $element => {
			$element.setAttribute('style', 'display : none;');
			const list = $element.closest('.pp-form-row')?.children;
			if (list !== undefined) {
				for (const formElement of Array.from(list)) {
					if (formElement.classList.contains(postcodeField)) {
						formElement.classList.add('hide');
					} else {
						formElement.setAttribute('style', 'flex-grow : 1;');
					}
				}
			}
		});
	} else {
		// Unhide the postal field.
		$qs<HTMLElement>(`${formIdentifier} .${postcodeField}`, $element => {
			$element.removeAttribute('style');
			const list = $element.closest('.pp-form-row')?.children;
			if (list !== undefined) {
				for (const formElement of Array.from(list)) {
					if (formElement.classList.contains(postcodeField)) {
						formElement.classList.remove('hide');
					} else {
						formElement.removeAttribute('style');
					}
				}
			}
		});
	}

	$qs<HTMLInputElement>(`${formIdentifier} [name="${section}_postcode"]`, $element => {
		$element.required = isRequiredField(`${section}_postcode`, fieldData, postcodeFieldInfo);
	});
}

function wcCityFieldRule(cityFieldInfo: WCFieldInfo, section: string, formIdentifier: string) {
	const fieldData = section === 'billing' ? GLOBAL.phpData?.billing_fields ?? [] : GLOBAL.phpData?.shipping_fields ?? [];
	$qs<HTMLInputElement>(`${formIdentifier} [name="${section}_city"]`, $element => {
		$element.required = isRequiredField(`${section}_city`, fieldData, cityFieldInfo);
	});
}

function unhideFields(fieldName: string, section: string, fieldData: WCFieldsData[], formIdentifier: string) {
	const fieldAttributeName = section + '_' + fieldName;
	// Make sure the fields are not required or required.
	$qs<HTMLInputElement>(`${formIdentifier} [name="${fieldAttributeName}"]`, $element => {
		$element.required = isRequiredField(`${fieldAttributeName}`, fieldData);
	});

	if (fieldName === 'city') {
		return;
	}

	const fieldOrder = section === 'billing' ? GLOBAL.phpData?.billing_fields_order ?? [] : GLOBAL.phpData?.shipping_fields_order ?? [];

	let enabledField = false;

	fieldOrder.forEach(order => {
		if ((fieldData?.[order]?.field_name === fieldAttributeName && fieldData[order]?.field_enable)) {
			enabledField = true;
		}
	});
	if (enabledField) {
		$qs<HTMLElement>(`${formIdentifier} .${fieldAttributeName}-field`, $element => {
			$element.removeAttribute('style');
			const list = $element.closest('.pp-form-row')?.children;
			if (list !== undefined) {
				for (const formElement of Array.from(list)) {
					if (formElement.classList.contains(`${fieldAttributeName}-field`)) {
						formElement.classList.remove('hide');
					} else {
						formElement.removeAttribute('style');
					}
				}
			}
		});
	}
}

export function collectCustomerFieldData(formIdentifier: string, requestedFields?: string[]) {
	const $form = $qs<HTMLFormElement>(formIdentifier);
	if ($form) {
		const formData = new FormData($form);
		const formFields = {...PeachPayCustomer.data().form_fields};

		let selector = '';
		if (requestedFields) {
			requestedFields.forEach((fieldName, index) => {
				selector += `${formIdentifier} [name="${fieldName}"]`;
				if (index < requestedFields.length - 1) {
					selector += ', ';
				}
			});
			Object.keys(formFields).forEach(fieldName => {
				if (!requestedFields.includes(fieldName)) {
					// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
					delete formFields[fieldName];
				}
			});
		} else {
			selector = `${formIdentifier} .pp-form-row :not(label):not(div):not(span):not([disabled]):not([name="off"])`;
		}

		$qsAll(selector, (element: HTMLElement) => {
			switch (element.tagName) {
				case 'INPUT': {
					const input = element as HTMLInputElement;
					if (input.type === 'radio' && input.checked) {
						const fieldName = input.closest('.pp-radio-field')?.firstElementChild?.getAttribute('for') ?? '';
						formFields[fieldName] = formEntry(formData, input.name);
					} else {
						formFields[input.name] = formEntry(formData, input.name);
					}

					break;
				}

				case 'SELECT': {
					const select = element as HTMLSelectElement;
					formFields[select.name] = formEntry(formData, select.name);
					break;
				}
			}
		});
		return formFields;
	}

	return null;
}

/**
 * Saves the customer information from the form fields to the redux store.
 */
export function syncCustomerFieldChanges(formIdentifier: string) {
	const formFields = collectCustomerFieldData(formIdentifier);
	if (formFields) {
		cleanFormFields(formFields);
		const refreshCountryFeatures = formFields['billing_country'] !== PeachPayCustomer.billing.country();
		store.dispatch(updateCustomerFields(formFields));
		if (refreshCountryFeatures) {
			// When billing country is changed and currency based on billing country, refresh currency switcher.
			window.dispatchEvent(new CustomEvent('pp-update-currency-switcher-feature'));
			window.dispatchEvent(new CustomEvent('pp-update-afterpay-branding'));
		}
	}
}

export async function loadCustomer(): Promise<void> {
	const customer = getCustomer();

	if (customer === null) {
		const inprogressCustomer = getCustomer(true);
		if (inprogressCustomer !== null) {
			store.dispatch(updateCustomer(inprogressCustomer));
			return;
		}

		const locationInfo = MerchantConfiguration.general.wcLocationInfoData();
		store.dispatch(updateCustomerFields({
			...PeachPayCustomer.data().form_fields,
			shipping_country: locationInfo?.customer_default_country ?? locationInfo?.store_country ?? '',
			shipping_state: locationInfo?.customer_default_state_or_province ?? '',
			billing_country: locationInfo?.customer_default_country ?? locationInfo?.store_country ?? '',
			billing_state: locationInfo?.customer_default_state_or_province ?? '',
		}));

		return;
	}

	cleanFormFields(customer.form_fields);
	store.dispatch(updateCustomer(customer));
}

/**
 * Saves the current customer state to the local storage.
 */
export function updateCustomerDataInBrowser(): void {
	if (Environment.customer.saveToBrowserSelected()) {
		const customer = PeachPayCustomer.data();

		setCustomer(customer);
	} else {
		clearCustomer();
	}
}

/**
 * Shows the correct dropdown/text-box with translated placeholder text based on the country
 *
 * @returns { void }
 */
export function renderCorrectProvinceField(phpData: WCPHPData, modalPage?: ModalPage): void {
	const merchantShipping = MerchantConfiguration.general.wcLocationInfoData();
	if (merchantShipping) {
		let name: string;
		let section: string;
		if (modalPage === undefined) {
			modalPage = Environment.modalUI.page();
		}

		if (modalPage === 'info') {
			name = '#pp-info-form';
			section = 'billing';
		} else if ((modalPage === 'shipping') && PeachPayCustomer.shipToDifferentAddress()) {
			name = '#pp-different-shipping-details';
			section = 'shipping';
		} else {
			return;
		}

		const countryElement = $qs<HTMLInputElement>(`${name} [name="${section}_country"]`);
		let country = '';

		if (countryElement) {
			country = countryElement.value;
		} else {
			country = phpData.wc_location_info?.customer_default_country ?? '';
		}

		const [defaultOption, label] = stateProvinceOrCounty(country);
		const stateOrProvinceOptions = merchantShipping.allowed_states_or_provinces[country] ?? {};

		if (stateOrProvinceOptions && Object.keys(stateOrProvinceOptions).length > 0) {
			// Enable select list
			$qs<HTMLSelectElement>(`${name} .${section}_state-field select`, $stateOrProvincesSelect => {
				$stateOrProvincesSelect.innerHTML = renderDropDownList(stateOrProvinceOptions, defaultOption);
				$stateOrProvincesSelect.disabled = false;
				$stateOrProvincesSelect.setAttribute('name', `${section}_state`);
				$stateOrProvincesSelect.required = isRequiredField(`${section}_state`, section === 'billing' ? phpData.billing_fields ?? [] : phpData.shipping_fields ?? [] as WCFieldsData[]);
				$stateOrProvincesSelect.value = section === 'billing' ? PeachPayCustomer.billing.state() : PeachPayCustomer.shipping.state();
				$stateOrProvincesSelect.classList.remove('hide');
				const chevron = $stateOrProvincesSelect.parentNode?.lastElementChild as HTMLElement;
				chevron?.classList.remove('hide');
			});
			$qs<HTMLLabelElement>(`${name} .${section}_state-field label.pp-select-label`, $element => {
				$element.textContent = label ?? defaultOption;
				$element.classList.remove('hide');
			});
			// Disable text box if present
			$qs<HTMLInputElement>(`${name} .${section}_state-field input`, $stateOrProvincesText => {
				$stateOrProvincesText.disabled = true;
				$stateOrProvincesText.setAttribute('name', 'off');
				$stateOrProvincesText.required = false;
				$stateOrProvincesText.value = '';
				$stateOrProvincesText.classList.add('hide');
			});
			$qs<HTMLLabelElement>(`${name} .${section}_state-field label:not(.pp-select-label)`)?.classList.add('hide');
		} else {
			// Disable select list
			$qs<HTMLSelectElement>(`${name} .${section}_state-field select`, $stateOrProvincesSelect => {
				$stateOrProvincesSelect.disabled = true;
				$stateOrProvincesSelect.setAttribute('name', 'off');
				$stateOrProvincesSelect.required = false;
				$stateOrProvincesSelect.classList.add('hide');
				const chevron = $stateOrProvincesSelect.parentNode?.lastElementChild as HTMLElement;
				chevron?.classList.add('hide');
			});
			$qs<HTMLLabelElement>(`${name} .${section}_state-field label.pp-select-label`)?.classList.add('hide');

			// Enable text box option
			$qs<HTMLInputElement>(`${name} .${section}_state-field input`, $stateOrProvincesText => {
				$stateOrProvincesText.disabled = false;
				$stateOrProvincesText.setAttribute('name', `${section}_state`);
				$stateOrProvincesText.required = isRequiredField(`${section}_state`, section === 'billing' ? phpData.billing_fields ?? [] : phpData.shipping_fields ?? [] as WCFieldsData[]);
				$stateOrProvincesText.value = '';
				$stateOrProvincesText.classList.remove('hide');
			});
			$qs<HTMLLabelElement>(`${name} .${section}_state-field label:not(.pp-select-label)`, $element => {
				$element.textContent = label ?? defaultOption;
				$element.classList.remove('hide');
			});
		}
	}
}

function renderCountryAndStateList(merchantLocationInfo?: WCLocationInfo) {
	if (!merchantLocationInfo) {
		console.warn('Warning: No WC Location info. Please update the PeachPay Plugin.');
		return;
	}

	const infoForms = [{name: '#pp-info-form', section: 'billing'}];
	if (PeachPayCustomer.shipToDifferentAddress()) {
		infoForms.push({name: '#pp-different-shipping-details', section: 'shipping'});
	}

	infoForms.forEach(({name, section}) => {
		const $countries = $qs<HTMLSelectElement>(`${name} [name="${section}_country"]`);
		// Render dropdown list if it hasn't been initialized yet.
		if ($countries && ($countries.children.length <= 1)) {
			const selectACountry = getLocaleText('Select a country');
			const countryOptions = merchantLocationInfo.allowed_countries;
			$countries.innerHTML = renderDropDownList(countryOptions, selectACountry);
			const country = section === 'billing' ? PeachPayCustomer.billing.country() : PeachPayCustomer.shipping.country();
			selectDropdown($countries, country || merchantLocationInfo.customer_default_country || merchantLocationInfo.store_country);

			// Auto select the only option valid if the store ships to only a single country.
			if ($countries.options.length === 2) {
				$countries.selectedIndex = 1; // Zero indexed
			}

			$countries.dispatchEvent(new Event('change'));
		}
	});
}

/**
 * Sets the customer fields to a given set of peachpay customer data.
 */
export function renderCustomerFields(customer: IPeachPayCustomer, page: null | 'info' | 'shipping' = null): void {
	let infoFormName: string;
	switch (page ?? Environment.modalUI.page()) {
		case 'info': {
			infoFormName = '#pp-info-form';
			break;
		}

		case 'shipping': {
			infoFormName = '#pp-different-shipping-details';
			break;
		}

		default: {
			return;
		}
	}

	Object.keys(customer.form_fields).forEach((key: string) => {
		if (customer.form_fields[key] !== undefined) {
			$qsAll<HTMLElement>(`${infoFormName} .${key}-field [name="${key}"]`, element => {
				switch (element.tagName) {
					case 'INPUT': {
						const input = element as HTMLInputElement;
						if (input.type === 'radio') {
							input.checked = input.value === customer.form_fields[key];
						} else {
							input.value = customer.form_fields[key] ?? '';
						}

						break;
					}

					case 'SELECT': {
						const select = element as HTMLSelectElement;
						select.value = customer.form_fields[key] ?? '';
						break;
					}
				}
			});
		}
	});
}

/**
 * Renders the peachpay address details
 */
function renderCustomerHeader(): void {
	let section = '';
	if (PeachPayCustomer.shipToDifferentAddress()) {
		section = 'shipping';
	} else {
		section = 'billing';
	}

	const shortAddress = getShortAddress();
	const fullAddress = getFullAddress(section);

	$qsAll('.pp-address', $element => {
		$element.innerHTML = shortAddress;
	});
	$qsAll('.pp-address-hidden', $element => {
		$element.innerHTML = fullAddress;
	});
}

export function getFullAddress(section: string): string {
	let customerInfoType = PeachPayCustomer.shipping;
	if (section === 'billing') {
		customerInfoType = PeachPayCustomer.billing;
	}

	// If country is Japan, then we need to display full state name rather than postal abbreviations
	if (customerInfoType.country() === 'JP') {
		const fullState = GLOBAL.phpData?.wc_location_info?.allowed_states_or_provinces?.['JP']?.[customerInfoType.state()];

		return [
			customerInfoType.postal(),
			[
				fullState ?? customerInfoType.state(),
				customerInfoType.city(),
			].filter(element => (element !== '')).join(' '),
			customerInfoType.address1(),
			customerInfoType.address2(),
		].filter(element => (element !== '')).join(', ');
	}

	return [
		[
			customerInfoType.address1(),
			customerInfoType.address2(),
		].filter(element => (element !== '')).join(' '),
		customerInfoType.city(),
		[
			customerInfoType.state(),
			customerInfoType.postal(),
		].filter(element => (element !== '')).join(' '),
		customerInfoType.country(),
	].filter(element => (element !== '')).join(', ');
}

export function getFullName(section: string): string {
	const customerInfoType = section === 'billing' ? PeachPayCustomer.billing : PeachPayCustomer.shipping;

	let fullName = '';
	if (customerInfoType.firstName()) {
		fullName += customerInfoType.firstName();
	}

	if (customerInfoType.firstName() && customerInfoType.lastName()) {
		fullName += ' ';
	}

	if (customerInfoType.lastName()) {
		fullName += customerInfoType.lastName();
	}

	return fullName;
}

export function getShortAddress(): string {
	if (PeachPayCustomer.shipping.country() === 'JP') {
		// Since Japan has a different address do Postal then address instead.
		return [
			PeachPayCustomer.shipping.postal(),
			PeachPayCustomer.shipping.address1(),
			PeachPayCustomer.shipping.address2(),
		].filter(element => (element !== '')).join(', ');
	}

	return [
		[
			PeachPayCustomer.shipping.address1(),
			PeachPayCustomer.shipping.address2(),
		].filter(element => (element !== '')).join(' '),
		PeachPayCustomer.shipping.postal(),
	].filter(element => (element !== '')).join(', ');
}

export function getCustomerEmail(): string {
	return PeachPayCustomer.billing.email();
}
